import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye, Trash2, Pencil, FileText, Plus, Loader2, AlertCircle } from 'lucide-react';

import {
  ApplicationWrapper,
  ApplicationContainer,
  HeaderSection,
  SectionTitle,
  SectionDescription,
  ActionButtons,
  DashboardButton,
  DocumentsPanel,
  PanelTitle,
  DocumentsList,
  DocumentItem,
  DocIconWrapper,
  DocInfo,
  DocName,
  DocActions,
  DocActionButton,
  GlobalError,
  LoadingState,
  EmptyState,
} from './Application.styles';

import RenameDocumentModal from './modals/RenameDocumentModal';

import { getApplication } from '../../services/application';
import {
  useSaveDocument,
  useDeleteDocument,
  useViewDocument,
} from '../UploadDocuments/hooks/Useuploaddocuments';
import { formatFileSize } from '../UploadDocuments/utils';
import {
  ApplicationMeta,
  ApplicationTitle,
  NewApplicationButton,
} from '../Dashboard/dashboard.styles';

const Application = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [folderName, setFolderName] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [docLoadingState, setDocLoadingState] = useState({});

  const [editingDoc, setEditingDoc] = useState(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  // ─── Fetch application ──────────────────────────────────────────────────────
  const {
    data: applicationData,
    isLoading,
    isError,
    error,
    refetch: refetchApplication,
  } = useQuery({
    queryKey: ['application', id],
    queryFn: () => getApplication(id),
    enabled: Boolean(id),
  });

  // Hydrate documents + folderName once the application loads
  // API response shape assumed similar to Processing: { list: { _id, folderName, pdfArray, ... } }
  useEffect(() => {
    if (applicationData) {
      const app = applicationData.list ?? applicationData; // handle both wrapped and flat shapes
      setFolderName(app.folderName ?? '');

      const mapped = (app.pdfArray ?? []).map((pdf, index) => ({
        id: pdf.documentId,
        name: pdf.fileName ?? pdf.documentName ?? pdf.name ?? `Document ${index + 1}`,
        size: pdf.fileSize ? formatFileSize(pdf.fileSize) : '—',
      }));
      setDocuments(mapped);
    }
  }, [applicationData]);

  // ─── Upload more (single file, isExisting + applicationId) ─────────────────
  const { mutate: uploadDocument, isPending: isUploading } = useSaveDocument(folderName, {
    onSuccess: () => {
      setGlobalError('');
      refetchApplication();
    },
    onError: (err) => {
      setGlobalError(err?.message || 'Failed to upload document. Please try again.');
    },
  });

  // ─── View / Delete (same services as Processing / UploadDocuments) ─────────
  const { mutate: viewDoc } = useViewDocument(folderName, {
    onSuccess: (data, variables) => {
      const { documentId } = variables;
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      const url = data?.documentUrl ?? data?.url;
      if (url) window.open(url, '_blank');
    },
    onError: (err, variables) => {
      const { documentId } = variables;
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      setGlobalError(err?.message || 'Failed to open document.');
    },
  });

  const { mutate: deleteDoc } = useDeleteDocument(folderName, {
    onSuccess: (_, variables) => {
      const { documentId } = variables;
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      setGlobalError('');
    },
    onError: (err, variables) => {
      const { documentId } = variables;
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      setGlobalError(err?.message || 'Failed to delete document.');
    },
  });

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleFileSelect = useCallback(
    (file) => {
      setGlobalError('');
      uploadDocument({ file, isExisting: true, applicationId: id });
    },
    [uploadDocument, id]
  );

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
      e.target.value = '';
    }
  };

  const handleUploadMore = () => {
    document.getElementById('application-file-input')?.click();
  };

  const handleView = useCallback(
    (doc) => {
      setDocLoadingState((prev) => ({ ...prev, [doc.id]: 'viewing' }));
      setGlobalError('');
      viewDoc({ documentId: doc.id });
    },
    [viewDoc]
  );

  const handleDelete = useCallback(
    (doc) => {
      const confirmed = window.confirm('Are you sure you want to delete this document?');
      if (!confirmed) return;
      setDocLoadingState((prev) => ({ ...prev, [doc.id]: 'deleting' }));
      setGlobalError('');
      deleteDoc({ documentId: doc.id });
    },
    [deleteDoc]
  );

  const handleEdit = useCallback((doc) => {
    setEditingDoc(doc);
    setIsRenameModalOpen(true);
  }, []);

  const handleCloseRenameModal = () => {
    setIsRenameModalOpen(false);
    setEditingDoc(null);
  };

  const handleDashboard = () => navigate('/');

  // ─── Loading / Error guards ────────────────────────────────────────────────

  if (isLoading) {
    return (
      <ApplicationWrapper>
        <ApplicationContainer>
          <LoadingState>
            <Loader2 size={24} className="spin" />
            <span>Loading application…</span>
          </LoadingState>
        </ApplicationContainer>
      </ApplicationWrapper>
    );
  }

  if (isError) {
    return (
      <ApplicationWrapper>
        <ApplicationContainer>
          <GlobalError role="alert">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {error?.message || 'Failed to load application.'}
          </GlobalError>
        </ApplicationContainer>
      </ApplicationWrapper>
    );
  }

  const app = applicationData?.list ?? applicationData ?? {};

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <ApplicationWrapper>
      <ApplicationContainer>
        <HeaderSection>
          <div>
            <SectionTitle>Application</SectionTitle>
          </div>
          <ActionButtons>
            <DashboardButton onClick={handleDashboard} type="button">
              Dashboard
            </DashboardButton>

            <NewApplicationButton
              onClick={handleUploadMore}
              disabled={isUploading || !folderName}
              type="button"
            >
              {isUploading ? (
                <>
                  <Loader2 size={14} className="spin" /> Uploading…
                </>
              ) : (
                <>
                  <Plus size={14} /> Upload More Documents
                </>
              )}
            </NewApplicationButton>

            <input
              id="application-file-input"
              type="file"
              accept=".pdf"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          </ActionButtons>
        </HeaderSection>

        <div>
          <ApplicationTitle> Title : {app.title}</ApplicationTitle>
          <ApplicationMeta> Desc. : {app.description}</ApplicationMeta>
        </div>

        {globalError && (
          <GlobalError role="alert">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {globalError}
          </GlobalError>
        )}

        <DocumentsPanel>
          <PanelTitle>
            Documents
            {documents.length > 0 && (
              <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--color-text-tertiary)' }}>
                ({documents.length})
              </span>
            )}
          </PanelTitle>

          {documents.length === 0 ? (
            <EmptyState>
              <p style={{ margin: 0 }}>No documents found for this application</p>
            </EmptyState>
          ) : (
            <DocumentsList>
              {documents.map((doc) => {
                const isViewing = docLoadingState[doc.id] === 'viewing';
                const isDeleting = docLoadingState[doc.id] === 'deleting';
                const isBusy = isViewing || isDeleting;

                return (
                  <DocumentItem key={doc.id}>
                    <DocIconWrapper>
                      <FileText size={16} />
                    </DocIconWrapper>

                    <DocInfo>
                      <DocName title={doc.name}>{doc.name}</DocName>
                    </DocInfo>

                    <DocActions>
                      <DocActionButton
                        title="View document"
                        onClick={() => handleView(doc)}
                        disabled={isBusy}
                        aria-label={`View ${doc.name}`}
                      >
                        {isViewing ? <Loader2 size={14} className="spin" /> : <Eye size={14} />}
                      </DocActionButton>

                      <DocActionButton
                        title="Rename document"
                        onClick={() => handleEdit(doc)}
                        disabled={isBusy}
                        aria-label={`Rename ${doc.name}`}
                      >
                        <Pencil size={14} />
                      </DocActionButton>

                      <DocActionButton
                        className="delete"
                        title="Delete document"
                        onClick={() => handleDelete(doc)}
                        disabled={isBusy}
                        aria-label={`Delete ${doc.name}`}
                      >
                        {isDeleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                      </DocActionButton>
                    </DocActions>
                  </DocumentItem>
                );
              })}
            </DocumentsList>
          )}
        </DocumentsPanel>
      </ApplicationContainer>

      <RenameDocumentModal
        isOpen={isRenameModalOpen}
        onClose={handleCloseRenameModal}
        doc={editingDoc}
        applicationId={id}
      />
    </ApplicationWrapper>
  );
};

export default Application;
